/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  RequestStripePayoutsInterface,
  StripePayoutsAPIResponse,
  StripePayoutsInitialProperties,
  StripePayoutsInterface,
} from '../../interface/stripe-payouts.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  ReplaceValueInFilter,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import stripePayoutsApi from '../../utils/api/stripe-payouts';

import { setNotificationMessage } from './NotificationManagementSlice';

export const fetchStripePayouts = createAsyncThunk<
  StripePayoutsAPIResponse,
  RequestStripePayoutsInterface
>(
  'stripe-payouts/fetchStripePayouts',
  async (data: RequestStripePayoutsInterface, { dispatch }) => {
    const response = await stripePayoutsApi
      .fetchStripePayouts(removeEmptyKeys({ ...data, submissionStatus: 'transferred' }))
      .then((response1) => response1)
      .catch((error) =>
        dispatch(
          setNotificationMessage({
            type: 'error',
            message: error?.response?.data?.message,
            code: error?.response?.data?.code,
            status: true,
          }),
        ),
      );
    return {
      ...response.data,
      results: response.data.results.map((paymentData: StripePayoutsInterface) => ({
        ...paymentData,
        transferredAt: paymentData.transferredAt ? ConvertUTCtoDate(paymentData.transferredAt) : '',
        fromDate: paymentData.fromDate ? ConvertUTCtoDate(paymentData.fromDate) : '',
        toDate: paymentData.toDate ? ConvertUTCtoDate(paymentData.toDate) : '',
        accountName: paymentData?.accountDetails?.accountName,
        email: paymentData?.accountDetails?.email,
        id: paymentData._id,
      })),
    };
  },
);

// a thunk to refresh stripe payouts
export const refreshApprovedStripePayouts = createAsyncThunk<
  StripePayoutsAPIResponse,
  RequestStripePayoutsInterface
>('stripe-payouts/refreshApprovedStripePayouts', async (data: RequestStripePayoutsInterface) => {
  const response = await stripePayoutsApi.fetchStripePayouts(
    removeEmptyKeys({ ...data, submissionStatus: 'transferred' }),
  );
  return {
    ...response.data,
    results: response.data.results.map((paymentData: StripePayoutsInterface) => ({
      ...paymentData,
      transferredAt: paymentData.transferredAt ? ConvertUTCtoDate(paymentData.transferredAt) : '',
      fromDate: paymentData.fromDate ? ConvertUTCtoDate(paymentData.fromDate) : '',
      toDate: paymentData.toDate ? ConvertUTCtoDate(paymentData.toDate) : '',
      accountName: paymentData?.accountDetails?.accountName,
      email: paymentData?.accountDetails?.email,
      id: paymentData._id,
    })),
  };
});

// a thunk to fetch devices filters
export const fetchApprovedStripePayoutsMethodFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('stripe-payouts/fetchApprovedStripePayoutsMethodFilters', async (_, { dispatch }) => {
  try {
    const response = await stripePayoutsApi.fetchStripePayoutsListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

const initialState: StripePayoutsInitialProperties = {
  payouts: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

export const approvedStripePayoutsSlice = createSlice({
  name: 'stripe-payouts',
  initialState,
  reducers: {
    /**
     * Update approved stripe payouts filters
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing filter updates.
     */
    updateApprovedStripePayoutsFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      } else if (payload.type === 'replace') {
        const { updatedCommonFilter, isFilterUpdated } = ReplaceValueInFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state The current slice state.
     */
    disableApprovedStripePayoutsApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The action reducer map builder.
   */
  extraReducers: (builder: ActionReducerMapBuilder<StripePayoutsInitialProperties>) => {
    builder
      .addCase(fetchStripePayouts.pending, (state: StripePayoutsInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchStripePayouts.fulfilled,
        (
          state: StripePayoutsInitialProperties,
          action: PayloadAction<StripePayoutsAPIResponse>,
        ) => {
          state.loading = false;
          state.payouts = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchStripePayouts.rejected, (state: StripePayoutsInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder.addCase(
      refreshApprovedStripePayouts.fulfilled,
      (state: StripePayoutsInitialProperties, action: PayloadAction<StripePayoutsAPIResponse>) => {
        state.payouts = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );
    builder
      .addCase(fetchApprovedStripePayoutsMethodFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchApprovedStripePayoutsMethodFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload].filter(
            (filters) => filters.key !== 'startDate_endDate',
          );
        },
      )
      .addCase(fetchApprovedStripePayoutsMethodFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  updateApprovedStripePayoutsFilters,
  disableApprovedStripePayoutsApplied,
  setSortData,
} = approvedStripePayoutsSlice.actions;

export default approvedStripePayoutsSlice.reducer;
